import type { RouterSourceDto } from './routerControllerDtos';

export const locationTypes = ['s3Clone'] as const;
export type LocationType = typeof locationTypes[number];

export const KELDA_SERVE_BUCKET = 'primary-root';
export const KELDA_UPLOAD_BUCKET = 'uploaded';
export const KELDA_WATCHFOLDERS_BUCKET = 'watchfolders';
export const KELDA_LOGS_BUCKET = 'logs';
export const KELDA_INDEXED_FOLDERS_BUCKET = 'indexedfolders';
export const KELDA_AVID_FOLDERS_BUCKET = 'avidfolders';
export const KELDA_LIVE_FOLDERS_BUCKET = 'livefolders';
export const KELDA_SEGMENT_FOLDERS_BUCKET = 'segmentfolders';

export const KELDA_BUCKET_ROOT = '/kelda/kelda-root';
export const KELDA_LOG_FOLDER = KELDA_BUCKET_ROOT + '/' + KELDA_LOGS_BUCKET;

// This is the value to use for newly created Kelda
export const KELDA_TMP_FOLDER = '/kelda/tmp';
export const KELDA_OLD_LOG_FOLDER = KELDA_LOG_FOLDER + '/old';
export const KELDA_LOG_CLEANUP_LOCK_FILE = KELDA_LOG_FOLDER + '/log_cleanup.lock';


export const DEFAULT_KELDA_CUTTER_RENDERING_CHUNKS = 2;

export const DEFAULT_LIVE_STABILITY_THRESHOLD_IN_MS = 10000;

export const DEFAULT_SCAN_INTERVAL_UPLOAD_FOLDER_IN_SECONDS = 5;
export const DEFAULT_SCAN_INTERVAL_WATCH_FOLDERS_IN_SECONDS = 30;
export const DEFAULT_SCAN_INTERVAL_AVID_FOLDERS_IN_SECONDS = 5 * 60;
export const DEFAULT_SCAN_INTERVAL_INDEXED_FOLDERS_IN_SECONDS = 5 * 60;
export const DEFAULT_SCAN_INTERVAL_LIVE_FOLDERS_IN_SECONDS = 30;
export const DEFAULT_SCAN_INTERVAL_SEGMENT_FOLDERS_IN_SECONDS = 5;

// If a folder scan interval is greater or equal than this value,
// the folder scanner will scan once and then release the request
// so that it will be picked up again later. Otherwise,
// the worker processing a request will keep scanning itself
// to avoid causing too much polling activity from all workers on
// all Kelda instances.
export const SINGLE_SCAN_INTERVAL_THRESHOLD_IN_SECONDS = 30;

/**
 * To be on the safe side in case of bugs, let's have a safety delay so that
 * we ping all the workers no matter what on a regular basis.
 *
 * For now, this threshold is arbitrarily set to 1 hour (not too often
 * to avoid losing the benefit of using a websocket instead of polling,
 * not too long to avoid having the system inactive for too long in case
 * of problems). We can adjust it later once the websocket implementation
 * has been battle tested.
 */
export const MAX_DELAY_WITHOUT_PING_IN_MS = 1 * 60 * 60 * 1000;

export type WorkerLogLevel = 'info' | 'debug' | 'silly';

export interface BucketFolder {

  /**
   * Name of the bucket folder, without the parent folder.
   */
  name: string;

  /**
   * true if the folder contains a safety file (see SAFETY_FILE)
   */
  safetyFile: boolean;
}


export interface RouterControllerInfo {
  routerId: string;
  routerControllerHost: string;
  routerControllerPort: number;

  // These are added from RouterDto when supervisor config is fetched
  // Not saved in kelda config entities themselves
  isNonExtendedRouter?: boolean;
  sourceListQueryNotSupported?: boolean;
  sourceList?: RouterSourceDto[];
}


/**
 * These fields together define the settings of one VDCP controller.
 */
export interface VdcpControllerInfo {
  host: string;

  // The port for the tcp connection
  port: number;

  // The input video port to record on
  videoPort: number;
}

export interface NamedVdcpControllerInfo extends VdcpControllerInfo {

  // The user defined name for the vdcp configuration.
  name: string;
}


/**
 * Here are the formats we support for playout. These are
 * non drop frame formats supported by Blackmagic cards that
 * give perfectly aligned video frames and 48000Hz audio frames.
 */
export type PlayoutFormat =
 '720x576:50i'
| '1920x1080:24p'
| '1920x1080:25p'
| '1920x1080:30p'
| '1920x1080:50p'
| '1920x1080:60p'
| '1440x1080:24p(16/9)'
| '1440x1080:25p(16/9)'
| '1440x1080:30p(16/9)'
| '1440x1080:50p(16/9)'
| '1440x1080:60p(16/9)'
| '1920x1080:50i'
| '1920x1080:60i'
| '1280x720:50p'
| '1280x720:60p'
| '3840x2160:60p'
| '3840x2160:50p'
| '3840x2160:30p'
| '3840x2160:25p';

export const NDI_DEFAULT_BUFFER_SIZE_MS = 5000;
export const NDI_DEFAULT_PREROLL_FRAME_COUNT = 2;
export const NDI_DEFAULT_INITIAL_TIMEOUT_MS = 5000;
export const NDI_DEFAULT_ACTIVE_TIMEOUT_MS = 1000;
export const NDI_DEFAULT_RECONNECT_TIMEOUT_MS = 30000;

export interface LivePreviewSettings {
  quality: number,
  width: number,
  height: number,
  frameRate: number | null,
}

export function defaultLivePreviewSettings(): LivePreviewSettings {
  return {
    quality: 6,
    width: 320,
    height: 180,
    frameRate: null,
  };
}


/**
 * A BucketFolder represents a root level bucket folder that Kelda is aware of.
 *
 * We use object notation to prepare for possibly having more information to provide, such as
 * file count, file size, disk space.
 */
export interface BucketRootFolder extends BucketFolder {
  /**
   * A list of folders that this BucketFolder contains.
   */
  bucketFolders: BucketFolder[];
}

export interface EncoderPresetFolder {
  /**
  * Name of the preset folder
  */
  name: string;
}

export const KELDA_AME_WATCHFOLDER_ROOT = '/kelda/AME';
export const KELDA_DVR_WATCHFOLDER_ROOT = '/kelda/DVR';

export interface EncoderWatchfolder {

  /**
   * The name of an AME watchfolder to appear under KELDA_AME_WATCHFOLDER_ROOT
   */
  name: string;

  /**
   * A list of preset folders that this watchfolder contains.
   */
  presetFolders: EncoderPresetFolder[];
}

export interface KeldaProxyVariantFlat {
  type: 'flat',
}

export interface KeldaProxyVariantSegmentedTs {
  type: 'segmented_ts',
  generatedMediaLocationId: string,
}

export type KeldaProxyVariant = KeldaProxyVariantFlat | KeldaProxyVariantSegmentedTs;

export const keldaSidecarFormats = <const>['json', 'xml', 'all'];
export type KeldaSidecarFormat = typeof keldaSidecarFormats[number];

export type LiveFolderLocalProxyVideoCodec = 'h264' | 'mpeg2';
export const DEFAULT_LOCAL_PROXY_VIDEO_CODEC : LiveFolderLocalProxyVideoCodec = 'mpeg2';

export interface LiveFolderLocalProxySettings {
  enabled: boolean;
  bitrateFor1080p25: number;
  gopSize: number;

  videoCodec?: LiveFolderLocalProxyVideoCodec,
}

export const KELDA_STORAGE_FOLDER_ROOT = '/kelda/storages';

/**
 * Image format related configurations
*/
export const SupportedStorageFolderImageContainerFormats = <const>[
  { label: 'PNG', value: 'png' }
];

/**
 * Audio format related configurations
*/
export const SupportedStorageFolderAudioFormats = <const>[
  { label: 'WAV', value: 'wav' },
  { label: 'MP3', value: 'mp3' },
];
export const SupportedStorageFolderAudioSampleRates = <const>[
  { label: '48000 Hz', value: '48000' },
  { label: '44100 Hz', value: '44100' }
];
export const SupportedStorageFolderAudioSampleSize = <const>[
  { label: '16 bit', value: '16' },
  { label: '32 bit', value: '32' },
];
export const SupportedStorageFolderAudioChannels = <const>[
  { label: 'Stereo', value: '2' },
  { label: 'Mono', value: '1' }
];
export const SupportedStorageFolderBitrates = <const>[
  { label: '128k', value: '128k' },
  { label: '192k', value: '192k' },
  { label: '256k', value: '256k' },
  { label: '320k', value: '320k' },
];

export interface StorageFolderFormatProperties {
  channels: typeof SupportedStorageFolderAudioChannels,
  sampleRates: typeof SupportedStorageFolderAudioSampleRates,
  sampleSizes?: typeof SupportedStorageFolderAudioSampleSize,
  bitrates?: typeof SupportedStorageFolderBitrates,
}

export interface BasicDto {
  id: string; // Is actually saved in the db as sk
  modifiedOn: string;
}

/**
 * The settings needed to configure Kelda as a MOS proxy talking
 * to an NCS.
 */
export interface MosConfig {

  /**
   * The ID of Kelda as a MOS device.
   */
  mosID: string;

  /**
   * The ID of the NCS system we want to talk to.
   */
  ncsID: string;

  /**
   * The IP address/hostname of the NCS system.
   */
  ncsHost: string;

  ncsLowerPort: number;
  ncsUpperPort: number;

 /**
   * If defined, all placeholders created by mosObjCreate from the
   * NCS will be put into this folder.
   */
  targetFolderId?: string;

  /**
   * If defined, a folder tree will be created for new running
   * orders under this root on roCreate messages, and placeholders created
   * for a running order will be put into the running order's folder.

   * Note that this value and targetFolderId does not exclude one another; if
   * both are set the placeholder will be put into two folders.
   */
  runningOrdersRootFolderId?: string;

  storageTransfersEnabled?: boolean;

  storageTransferSettings?: {

    /**
     * The ID of the storage to transfer content to.
     */
    storageId: string,
  }

  includeObjProxyPath?: boolean
}

export const DEFAULT_MOS_LOWER_PORT = 10540;
export const DEFAULT_MOS_UPPER_PORT = 10541;


export interface MinioConfigDto extends BasicDto {
  endpoint: string;
  bucketName: string;

  // Only included optionally from some endpoints.
  keldaName?: string;
  keldaId?: string;
  accessKey?: string;

  // Whether to delete from the bucket when Mimir item is permanently deleted
  deleteEnabled: boolean;
  /**
   * stores array of groups id which has the permission to see the location
   * VisibleTo property already existed in IngestStorage, so to keep consistency using the name visibleToGroupList everywhere instead of visibleTo
   */
  visibleToGroupList?: string[],

  lastS3ServerKeysUploadedAt?: string;
}

export interface UpdateMinioConfigDto {
  id: string;
  bucketName: string;
  endpoint: string;
  keys?: {
    accessKey: string;
    accessSecret: string;
  }
  deleteEnabled?: boolean;
  /**
   * stores array of groups id which has the permission to see the location
   * VisibleTo property already existed in IngestStorage, so to keep consistency using the name visibleToGroupList everywhere instead of visibleTo
   */
  visibleToGroupList?: string[] | null;

  uploadedS3ServerKeys?: true;
}

export interface CreateMinioConfigDto {
  // When creating a minio configuration, we allow
  // either passing an explicit bucket name or
  // passing no bucket name in which case we default
  // to auto-creating the buckets 'primary-root' and
  // 'uploaded'
  bucketName?: string;
  endpoint: string;
  keys: {
    accessKey: string;
    accessSecret: string;
  }

  // Whether to delete from the bucket when Mimir item is permanently deleted
  deleteEnabled: boolean;
  /**
   * stores array of groups id which has the permission to see the location
   * VisibleTo property already existed in IngestStorage, so to keep consistency using the name visibleToGroupList everywhere instead of visibleTo
   */
  visibleToGroupList?: string[] | null;
}

export interface ActiveNodeNotificationConfig {
  minimumNodeCount: number;
}

export const newMinioConfiguration = (): CreateMinioConfigDto => {
  return {
    keys: {
      accessKey: '',
      accessSecret: '',
    },
    endpoint: '',
    deleteEnabled: false,
    visibleToGroupList: null,
  };
};

export const isBucketFolders = (obj: unknown): obj is BucketRootFolder[] => {
  return Array.isArray(obj) && obj.every(folder => typeof folder.name === 'string');
};

export const isEncoderWatchfolders = (obj: unknown): obj is EncoderWatchfolder[] => {
  return Array.isArray(obj) && obj.every(folder => typeof folder.name === 'string');
};

// This value is meant to be used as the primary highres location ID when
// we want to configure Kelda to upload highres to our bucket
export const MIMIR_INCOMING_MEDIA_BUCKET = 'MIMIR_INCOMING_MEDIA_BUCKET';

// This value is meant to be used as the primary highres location ID when
// we want to configure Kelda to upload highres to the default upload location,
// that could be either a location defined by the customer or our incoming
// media bucket if the customer hasn't configured a default upload location
export const DEFAULT_UPLOAD_LOCATION = 'DEFAULT_UPLOAD_LOCATION';


export const DEFAULT_SM_MAX_CHUNK_SIZE_MB = 25;

export interface OnPremiseLocation {
  /**
   * For an import workflow, the ID is pointing to an ingest storage configuration;
   * otherwise it points to a minio configuration attached to a Kelda configuration
   */
  id: string;
  bucketName: string;
}


/**
 * This type represents the different kinds of folders Kelda
 * can read input files from.
 */
export type KeldaInputFolderType = 'upload' | 'watchFolder' | 'indexedFolder'
  | 'avidFolder' | 'avidBookkeepingFolder' | 'liveFolder';

export const KEEP_HIGHRES_ON_PREMISE = 'KEEP_HIGHRES_ON_PREMISE';

/**
 * Representation of path mapping of a file in a bucket inside kelda to a path
 * that is accessible by AME.
 */
export interface ConformSourceFolder {
  /**
   * A prefix that has to be added to the ingest source path stored on the item
   * so that kelda can access it. May or may not be separator('/') terminated.
   *
   * Example: for indexed folders, the ingestSourcePath is relative to the bucket.
   * - ingestSourcePath: "my-index-folder/file.mp4"
   * - Actual kelda accessible path: "/kelda/kelda-root/indexedfolders/my-index-folder/file.mp4"
   *
   * So, to get the full path we have to add "/kelda/kelda-root/indexedfolders/" as the prefix.
   */
  pathPrefixFromKeldaPov: string;

  /**
   * Path to a configured folder inside one of the buckets (indexed, live) that AME can access.
   * May or may not be separator('/' or, '\\') terminated.
   *
   * As kelda runs inside a docker container, path to a folder inside kelda and the actual path
   * inside the host machine is most probably different.
   *
   * Example:
   * - Indexed folder path (relative to bucket): "my-index-folder"
   * - Full kelda accessible path: "/kelda/kelda-root/indexedfolders/my-index-folder"
   * - Path to the folder in a Windows machine where AME is running: "C:\kelda\kelda-root\indexedfolders\my-index-folder"
   */
  pathToFolderFromAmePov: string;

  /**
   * Prefix that needs to be removed from item's source path so that it can be joined
   * to "pathToFolderFromAmePov" to construct a full AME reachable path to the file. Must be
   * separator('/') terminated.
   *
   * Example:
   * - ingestSourcePath: "my-index-folder/file.mp4"
   * - pathToFolderFromAmePov: "C:\kelda\kelda-root\indexedfolders\my-index-folder"
   * - Prefix to strip from full kelda path so it can be joined to ame path: "my-index-folder/"
   */
  prefixToStripFromSourcePath: string;
}

export type ArchiveUploadConfig = {
  // If true uploads will try to use s3 transfer acceleration
  useS3Acceleration: boolean;

  // These size and concurrency parameters are used as
  // input to the jobUpload code.
  uploadPartSizeMb?: number;
  inflightTargetMb?: number;
  maxConcurrency?: number;
  minConcurrency?: number;
};

export interface PackageReference {
  name: string,
  version?: string,
  // If true, means that the dependency being missing from the
  // package list should not be treated as an error. This is meant
  // to be used to handle gracefully at runtime dependencies that
  // are not supported by a given architecture like the braw/r3d decode
  // utilities that we cannot compile for ARM for lack of available ARM SDKs
  optional?: boolean,
}

export interface PackageDefinition {
  /**
   * The name of the package, like 'init'. This identifier
   * is the one used by a task to point to the package containing
   * the command it should run.
   */
  name: string;

  /**
   * We might be interested in keeping different versions of one
   * dependency. Because of this, dependencies may have a version
   * associated with it as well as a name
   */
  version?: string;

  /**
   * A hash value of the package zip. If this value changes,
   * the supervisor will fetch the package again.
   */
  hash: string;

  /**
   * The url to obtain the package file from.
   * If the url is a relative one, it is relative to the package
   * list url this package definition was read from.
   */
  url: string;


  /**
   * List of dependencies for packages. This is used two places.
   * The supervisor will use this list to look up in packages.json
   * which additional packages needs to be install for the current
   * package.
   * Additionally each worker may query this list to obtain the
   * install directory for a given dependency. i.e "ffmpeg"
   */
  dependencies?: PackageReference[],

  /**
   * This field will be injected into the packages.json before
   * being communicated to the workers so that the workers have
   * a mechanism for obtaining the full path to dependency
   * executables.
   */
  installDirectory?: string,
}

/**
 * Utility function to obtain the full name of hte package. If a
 * package is versioned this will include the version
 */
export function getFullPackageName(pkg: PackageDefinition) {
  if (pkg.version !== undefined) {
    return `${pkg.name}.${pkg.version}`;
  } else {
    return `${pkg.name}`;
  }
}

/**
 * The name of the optional manifest to be found in the root of
 * a task package.
 */
export const PACKAGE_MANIFEST = 'manifest.json';


export type Command = string[];
export type DependencyName = string;
export type DependencyVersion = string;
/**
 * This interface describes the content of the package
 * manifest, if any.
 */
export interface PackageManifest {
  /**
   * This optional property represents a command to be executed inside
   * the package directory after it has been unzipped. For instance, it
   * can be used to install npm dependencies:
   *
   *   ['npm', 'i', 'foo']
   */
  postInstall?: string[];
}
